// Registry der Tab-Typen (docs/shell.md).
//
// Die Huelle kennt KEINE einzige Domaene. Sie kennt nur diesen Vertrag:
// ein Tab ist Typ + Identitaet, und zu jedem Typ gibt es hier einen
// Eintrag, der weiss, wie der Tab heisst und welche Komponente ihn
// zeichnet.
//
// Warum eine Registry statt einer {#if typ === "..."}-Kette in der
// Huelle: eine Kette macht die Huelle von jeder Domaene abhaengig, und
// jede neue kostet einen Eingriff mitten im Rahmen. Dasselbe Muster
// benutzt das Backend schon fuer Tasks (taskRegistry.Register).
//
// Die Eintraege selbst stehen in panels.ts -- einem Manifest, analog zu
// routes.ts. Die Huelle importiert nur diese Datei, nie eine Domaene.

import type { Component } from "svelte";

import type { IconName } from "../Icon.svelte";

/** Die Registry haelt Panels VERSCHIEDENER Prop-Formen nebeneinander.
 *  An dieser einen Stelle muss die Typisierung deshalb aufgeben --
 *  gepruefte Typen gibt es beim Registrieren (PanelDef<P> unten), nicht
 *  beim Herausholen. Die Huelle reicht die Params durch, ohne sie zu
 *  kennen; das ist der Preis dafuer, dass sie keine Domaene kennt. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PanelComponent = Component<any, any, any>;

export interface PanelDef<P = Record<string, unknown>> {
  /** Klartext-Titel des Tabs, abgeleitet aus der Identitaet. */
  title: (params: P) => string;
  /** Zweite Zeile im Tab bzw. in der Kopfzeile (optional). */
  subtitle?: (params: P) => string;
  /** Icon im Tab. Kann von der Identitaet abhaengen (eine Seite traegt
   *  das Icon ihrer Sektion), darum eine Funktion. */
  icon?: IconName | ((params: P) => IconName);
  /** Bereich (SectionId), in dem das Ding liegt -- traegt die
   *  Bereichsfarbe an Tab und Flaeche (app.css „Farbe"). Ohne Angabe
   *  bleibt es beim Akzent. */
  bereich?: string | ((params: P) => string | null);
  /** Lazy geladen -- ein Panel, das nie geoeffnet wird, kostet nichts. */
  load: () => Promise<{ default: PanelComponent }>;
}

// Intern typlos (siehe PanelComponent), nach aussen generisch.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const registry = new Map<string, PanelDef<any>>();

export function registerPanel<P extends Record<string, unknown>>(
  typ: string,
  def: PanelDef<P>,
): void {
  if (registry.has(typ)) {
    // Doppelte Registrierung ist immer ein Fehler, kein Ueberschreiben:
    // sonst gewinnt je nach Import-Reihenfolge mal die eine, mal die
    // andere Definition -- und das faellt erst im Betrieb auf.
    throw new Error(`shell: Tab-Typ "${typ}" ist schon registriert`);
  }
  registry.set(typ, def);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getPanel(typ: string): PanelDef<any> | undefined {
  return registry.get(typ);
}

export function knownPanelTypes(): string[] {
  return [...registry.keys()].sort();
}

/** titelVon / iconVon loesen die beiden Felder auf, die auch Funktionen
 *  sein duerfen. Damit muss das nicht an jeder Anzeigestelle stehen. */
export function titelVon(typ: string, params: Record<string, unknown>): string {
  const def = getPanel(typ);
  if (!def) return typ;
  try {
    return def.title(params);
  } catch {
    // Ein kaputter Titel darf die Tab-Leiste nicht mitreissen.
    return typ;
  }
}

export function iconVon(
  typ: string,
  params: Record<string, unknown>,
): IconName | undefined {
  const def = getPanel(typ);
  if (!def?.icon) return undefined;
  return typeof def.icon === "function" ? def.icon(params) : def.icon;
}

export function bereichVon(
  typ: string,
  params: Record<string, unknown>,
): string | undefined {
  const def = getPanel(typ);
  if (!def?.bereich) return undefined;
  try {
    return (typeof def.bereich === "function" ? def.bereich(params) : def.bereich) ?? undefined;
  } catch {
    return undefined;
  }
}

export function untertitelVon(
  typ: string,
  params: Record<string, unknown>,
): string | undefined {
  const def = getPanel(typ);
  if (!def?.subtitle) return undefined;
  try {
    return def.subtitle(params);
  } catch {
    return undefined;
  }
}
